import type { LayerModel, Selection, ViewScope, WorkScope } from '../core/types';
import { selectedFragmentIds } from './selection';
import { measureArea } from './measurement';

export function fincaIdForSelection(model: LayerModel | null, selection: Selection): string | null {
  if (!model || !selection) return null;
  if (selection.kind === 'finca') return selection.id;
  if (selection.kind === 'suerte') return model.suertes.get(selection.id)?.fincaId ?? null;
  if (selection.kind === 'tablon') {
    const suerteId = model.tablones.get(selection.id)?.suerteId;
    return suerteId ? model.suertes.get(suerteId)?.fincaId ?? null : null;
  }
  return model.fragmentsById.get(selection.id)?.fincaId ?? null;
}

export function scopedFragmentIds(
  model: LayerModel | null,
  scope: ViewScope,
  activeFincaId: string | null,
  selection: Selection,
): ReadonlySet<string> {
  if (!model) return new Set();
  if (scope === 'selection') return selectedFragmentIds(model, selection);
  if (scope === 'finca') return new Set(model.fincas.get(activeFincaId ?? '')?.fragmentIds ?? []);
  return new Set(model.fragments.map((fragment) => fragment.id));
}

export const emptyWorkScope = (): WorkScope => ({ fincaIds: [], suerteIds: [], tablonIds: [] });

export function normalizeWorkScope(model: LayerModel | null, scope: WorkScope): WorkScope {
  if (!model) return emptyWorkScope();
  const fincaIds = [...new Set(scope.fincaIds)].filter((id) => model.fincas.has(id));
  const fincaSet = new Set(fincaIds);
  const suerteIds = [...new Set(scope.suerteIds)].filter((id) => {
    const suerte = model.suertes.get(id);
    return Boolean(suerte && fincaSet.has(suerte.fincaId));
  });
  const suerteSet = new Set(suerteIds);
  const tablonIds = [...new Set(scope.tablonIds)].filter((id) => {
    const tablon = model.tablones.get(id);
    return Boolean(tablon && suerteSet.has(tablon.suerteId));
  });
  return { fincaIds, suerteIds, tablonIds };
}

export function workScopeFragmentIds(model: LayerModel | null, requested: WorkScope): ReadonlySet<string> {
  if (!model) return new Set();
  const scope = normalizeWorkScope(model, requested);
  // An empty hierarchical scope is the efficient, serializable ALL state for the
  // Maestro. It must not materialize thousands of selected finca ids.
  if (!scope.fincaIds.length) return new Set(model.fragments.map(({ id }) => id));
  const ids = scope.tablonIds.length
    ? scope.tablonIds.flatMap((id) => model.tablones.get(id)?.fragmentIds ?? [])
    : scope.suerteIds.length
      ? scope.suerteIds.flatMap((id) => model.suertes.get(id)?.fragmentIds ?? [])
      : scope.fincaIds.flatMap((id) => model.fincas.get(id)?.fragmentIds ?? []);
  return new Set(ids);
}

export function workScopeSummary(model: LayerModel | null, requested: WorkScope): { label: string; fragmentCount: number; suerteCount: number; areaHa: number } {
  if (!model) return { label: 'Carga un Maestro CASUR', fragmentCount: 0, suerteCount: 0, areaHa: 0 };
  const scope = normalizeWorkScope(model, requested);
  const fragmentIds = workScopeFragmentIds(model, scope);
  const suerteIds = new Set([...fragmentIds].flatMap((id) => {
    const suerteId = model.fragmentsById.get(id)?.suerteId;
    return suerteId ? [suerteId] : [];
  }));
  const areaHa = [...fragmentIds].reduce((sum, id) => {
    const geometry = model.fragmentsById.get(id)?.feature.getGeometry();
    return sum + (geometry?.getType().includes('Polygon') ? measureArea(geometry).hectares : 0);
  }, 0);
  const level = !scope.fincaIds.length ? 'Todo CASUR' : scope.tablonIds.length ? `${scope.tablonIds.length} tablón(es)` : scope.suerteIds.length ? `${scope.suerteIds.length} suerte(s)` : `${scope.fincaIds.length} finca(s)`;
  return { label: level, fragmentCount: fragmentIds.size, suerteCount: suerteIds.size, areaHa };
}

export function workScopeEntityIds(model: LayerModel | null, requested: WorkScope): { fincaIds: ReadonlySet<string>; suerteIds: ReadonlySet<string>; tablonIds: ReadonlySet<string> } {
  const fragmentIds = workScopeFragmentIds(model, requested);
  const fincaIds = new Set<string>(); const suerteIds = new Set<string>(); const tablonIds = new Set<string>();
  fragmentIds.forEach((id) => {
    const fragment = model?.fragmentsById.get(id);
    if (fragment) { fincaIds.add(fragment.fincaId); suerteIds.add(fragment.suerteId); tablonIds.add(fragment.tablonId); }
  });
  return { fincaIds, suerteIds, tablonIds };
}
